import { useEffect } from "react";
import { X } from "lucide-react";
import { useAppTranslation } from "@/i18n/useAppTranslation";

export interface FrontierModalProps {
  sym: string;
  amount?: number;
  onClose: () => void;
}

export function FrontierModal({ sym, amount, onClose }: FrontierModalProps) {
  const { t } = useAppTranslation();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const techs = [
    { img: "direct-air-capture.webp", title: "Direct Air Capture", desc: "Machines that pull CO₂ directly from the atmosphere and store it underground permanently." },
    { img: "rock-weathering.webp", title: "Enhanced Rock Weathering", desc: "Accelerating the natural process of minerals absorbing CO₂ from the air." },
    { img: "biomass.webp", title: "Biomass Carbon Removal", desc: "Converting plant biomass into stable forms that lock away carbon for millennia." },
    { img: "ocean-removal.webp", title: "Direct Ocean Removal", desc: "Removing dissolved CO₂ from ocean water, allowing the ocean to absorb more from the atmosphere." },
  ];

  return (
    <div
      data-testid="frontier-modal-backdrop"
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/60 sm:items-center sm:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={t("checkout.frontier_title")}
    >
      <div
        data-testid="frontier-modal-content"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-h-screen sm:max-h-[90vh] sm:max-w-2xl overflow-y-auto sm:rounded-2xl shadow-2xl text-[color:var(--color-brand-tint)]"
        style={{
          background:
            "linear-gradient(180deg, var(--color-brand-deep) 0%, var(--color-brand-night) 100%)",
        }}
      >
        <div
          className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-4 sm:px-7"
          style={{
            background:
              "linear-gradient(180deg, var(--color-brand-deep) 0%, color-mix(in oklab, var(--color-brand-deep) 90%, transparent) 100%)",
            borderBottom: "1px solid color-mix(in oklab, var(--color-brand-divider) 60%, transparent)",
          }}
        >
          <img
            src={`${import.meta.env.BASE_URL || "/"}images/frontier/logo-light.png`}
            alt={t("checkout.frontier_logo_alt")}
            className="h-6 w-auto brightness-0 invert"
            style={{ filter: "brightness(0) invert(1)" }}
          />
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="cursor-pointer rounded-full p-1.5 transition-colors hover:bg-white/10"
            style={{ color: "var(--color-brand-tint)" }}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 pb-7 pt-5 sm:px-7">
          <h3 className="font-sans text-2xl font-bold leading-tight" style={{ color: "#fff" }}>
            {t("checkout.frontier_title")}
          </h3>
          {typeof amount === "number" && amount > 0 && (
            <p className="mt-1 text-xs uppercase tracking-[0.14em]" style={{ color: "var(--color-brand-dm)" }}>
              {t("checkout.frontier_your_contribution", { amount: `${sym}${amount.toFixed(2)}` })}
            </p>
          )}
          <p className="mt-3 text-sm leading-relaxed" style={{ color: "color-mix(in oklab, var(--color-brand-tint) 85%, transparent)" }}>
            {t("checkout.frontier_contribution_funds")}
          </p>

          <div
            className="mt-5 rounded-xl p-4"
            style={{
              background: "color-mix(in oklab, var(--color-brand-light) 22%, transparent)",
              border: "1px solid color-mix(in oklab, var(--color-brand-divider) 70%, transparent)",
            }}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em]" style={{ color: "var(--color-brand-dm)" }}>
              {t("checkout.frontier_where_goes")}
            </p>
            <p className="mt-1.5 text-xs leading-relaxed" style={{ color: "color-mix(in oklab, var(--color-brand-tint) 92%, transparent)" }}>
              {t("checkout.frontier_offtake")}
            </p>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {techs.map(({ img, title, desc }) => (
              <div
                key={title}
                className="overflow-hidden rounded-xl"
                style={{
                  background: "color-mix(in oklab, var(--color-brand-light) 14%, transparent)",
                  border: "1px solid color-mix(in oklab, var(--color-brand-divider) 55%, transparent)",
                }}
              >
                <img
                  src={`${import.meta.env.BASE_URL || "/"}images/frontier/${img}`}
                  alt={title}
                  className="h-28 w-full object-cover"
                  loading="lazy"
                />
                <div className="p-3">
                  <h4 className="font-sans text-sm font-semibold" style={{ color: "#fff" }}>{title}</h4>
                  <p className="mt-1 text-[11px] leading-relaxed" style={{ color: "color-mix(in oklab, var(--color-brand-tint) 80%, transparent)" }}>
                    {desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-5 text-[11px] leading-relaxed" style={{ color: "color-mix(in oklab, var(--color-brand-tint) 70%, transparent)" }}>
            {t("checkout.frontier_founded")}
          </p>

          <a
            href="https://frontierclimate.com"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-block text-xs font-medium underline-offset-2 hover:underline"
            style={{ color: "var(--color-brand-dm)" }}
          >
            {t("checkout.frontier_learn_more")}
          </a>
        </div>
      </div>
    </div>
  );
}

export default FrontierModal;
